/**
 * ATL-1445 stage 3, P0 (derived since P4) — IS THIS BUNDLE ABLE TO BE A WRITE-CUTOVER TILL?
 *
 * The write cutover (`docs/technical/atlas-api-v1-write-cutover.md`) makes Supabase the write master
 * for one organization. Every device of that organization must then send its W-domain writes through
 * the `writeW` seam (backend first, outbox v2) instead of Firestore first. A device whose bundle has
 * no seam keeps writing Firestore first after the flip — a second master, the one state the design
 * forbids. The `write-bundle-coverage` gate clause therefore has to PROVE, per organization, that every
 * device seen in the window runs a bundle that can honour the flip.
 *
 * This file is the bundle's own answer to that question, compiled in. It is deliberately a constant:
 * the answer is a property of the CODE in the bundle, not of the organization, the flag or the
 * network, so nothing at runtime may change it.
 *
 * SINCE P4 THE ANSWER IS DERIVED, NOT HAND-SET. It is a pure function of two compiled-in facts:
 *   - `WRITE_W_SEAM` below — what the seam (`writeW.ts`) can do. P4 shipped the seam in PASS-THROUGH
 *     only (`backendFirst: false`); P9 added the backend-first branch (`writeWBackendFirst.ts`) and set
 *     `backendFirst: true`, seam version 2;
 *   - `W_WRITER_MANIFEST` — generated from the W-writer registry, whose `converted` flags the registry
 *     gate verifies against the source (the P4 bypass check, `writeWBypassProblems`).
 * TODAY THE ANSWER IS `false`, reason `writers-unconverted`: the seam can go backend-first, but the
 * manifest shows W writers still unconverted (its `converted` / `total` are the live count — P9a converts
 * the sales writers only). A P4–P8 bundle says `seam-pass-through`; a bundle before P4 says `seam-absent`
 * (seam version 0).
 *
 * WHO CHANGES THIS, AND THE RULE THEY MUST KEEP: `capable` may be `true` only when this bundle would
 * refuse to write any W-domain document Firestore-first for a cut-over organization. The PR that adds
 * the backend-first branch (P9) sets `backendFirst: true` and bumps `version`; capability then follows
 * the manifest by itself. A bundle that is "mostly" converted is `false` with a reason — partial
 * capability is not capability (§6: a partial flip exists only on staging, behind a staging-only build
 * constant, and never reports capable). P9b built that constant (`writeWPreview.ts`): a preview build's
 * capability carries `preview: true` next to `capable: false` — the session may enter Supabase mode for
 * a Supabase-master organization (`decideSessionWriteMode`), its unconverted writers refuse there
 * (`assertWWriterAvailable`), it refuses before its mode is pinned (`currentSessionWriteMode`, as a capable
 * bundle does — ATL-1678), and nothing that reads `capable` (the gate event, the startup outbox look)
 * treats it as capable.
 *
 * How the value reaches the gate, and why it is an EVENT rather than a PostHog super property, is in
 * `global/analytics/writeBundleCapability.ts`.
 */
import { W_WRITER_MANIFEST } from './wWriterManifest.generated'
import type { WWriterManifest } from './wWriterRegistry'
import { WRITE_W_PREVIEW_BUILD, confirmWriteWPreviewAtRuntime } from './writeWPreview'

/** Why a bundle is not write-cutover capable. Stable strings: the gate prints and groups by them. */
export type WriteWIncapableReason =
  /** This bundle has no `writeW` seam at all (every bundle before stage-3 P4). Kept: old bundles report it. */
  | 'seam-absent'
  /**
   * The seam exists but has no backend-first branch (P4 until P9): it can only pass through to
   * today's Firestore-first bodies, so it cannot honour a Supabase-master organization whatever the
   * manifest says.
   */
  | 'seam-pass-through'
  /** The seam can go backend-first, but the compiled-in manifest shows a W writer not converted. */
  | 'writers-unconverted'

export interface WriteWCapability {
  /** True only when every W writer in this bundle goes through the seam (see the rule above). */
  readonly capable: boolean
  /**
   * The seam's contract version. 0 = no seam. Bumped whenever what "capable" promises changes, so
   * the gate can require a MINIMUM seam version rather than trusting any `capable: true` forever.
   */
  readonly seamVersion: number
  /** Null when capable; otherwise why not. */
  readonly reason: WriteWIncapableReason | null
  /**
   * Identifier of the compiled-in W-writer coverage manifest (P1 generates it), or null while no
   * manifest exists. Lets the gate name exactly which writer set a capable bundle was checked against.
   */
  readonly manifest: string | null
  /**
   * P9b — true only in a STAGING cut-over preview build (`writeWPreview.ts`) whose seam can go
   * backend-first and whose manifest is incomplete. Never true together with `capable` (a complete
   * manifest needs no preview), never true in a production build (compiled out). Optional so that a
   * capability built before P9b — and every test literal — reads as "not a preview".
   */
  readonly preview?: boolean
}

/**
 * The seam's own declaration of what it can do. `writeW.ts` must agree with it: its Supabase branch
 * rejects with `WriteWBackendFirstUnavailableError` whenever `backendFirst` is false (asserted by
 * `global/Write/__tests__/writeW.test.js`).
 *
 *  - `version`: 1 = the P4 seam (write-mode resolution, the persisted last-known flag, the session
 *    pin, pass-through). 2 = P9: the backend-first branch on outbox v2 (`writeWBackendFirst.ts`), the
 *    lazy plan (`WOperation.plan`), and the refuse-offline send of invariant 12.
 *  - `backendFirst`: true since P9. Capability then follows the manifest by itself: while any W writer
 *    is unconverted (P10–P13), the reason is `writers-unconverted` and no session can be in Supabase
 *    mode — except, on staging, a cut-over preview build (P9b, `writeWPreview.ts`; design §18).
 */
export interface WriteWSeamDeclaration {
  readonly version: number
  readonly backendFirst: boolean
}

export const WRITE_W_SEAM: WriteWSeamDeclaration = Object.freeze({ version: 2, backendFirst: true })

/** The manifest's identity as the capability event reports it: its registry digest. */
export const writeWManifestId = (manifest: Pick<WWriterManifest, 'registryDigest'>): string =>
  `w-manifest-${manifest.registryDigest}`

/**
 * The capability a seam declaration and a manifest imply. Capable only when BOTH hold: the seam can
 * go backend-first, and the manifest shows every W writer converted (`allConverted`, which the
 * registry gate verifies writer by writer — `writeWBypassProblems` in `scripts/wWriterCensus.ts`).
 * The seam is checked first: a pass-through seam is not capable however complete the manifest.
 *
 * `previewBuild` (P9b) is `WRITE_W_PREVIEW_BUILD`, confirmed at runtime (S3-2), for the shipped constant. It can only ever ADD
 * `preview: true` to a bundle that is incapable for exactly one reason — `writers-unconverted`; it
 * never changes `capable`, `reason` or anything the gate reads. A pass-through seam is not a preview
 * either: it has no backend-first branch to preview.
 */
export const deriveWriteWCapability = (
  seam: WriteWSeamDeclaration,
  manifest: Pick<WWriterManifest, 'allConverted' | 'registryDigest'>,
  previewBuild = false,
): WriteWCapability => {
  const reason: WriteWIncapableReason | null = !seam.backendFirst
    ? 'seam-pass-through'
    : !manifest.allConverted
      ? 'writers-unconverted'
      : null
  return Object.freeze({
    capable: reason === null,
    seamVersion: seam.version,
    reason,
    manifest: writeWManifestId(manifest),
    preview: previewBuild && reason === 'writers-unconverted',
  })
}

/**
 * The running Firebase app, loaded LAZILY: only a preview build calls this (the `&&` below), so no other
 * build loads the Firebase module from here or reads anything (P9b review 2, S3-2).
 */
const loadFirebaseApp = (): unknown =>
  // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports -- lazy on purpose: evaluated only in a preview build (S3-2)
  require('../Firebase/firebaseFirestore').app

/**
 * The shipped capability. `preview` is the compiled-in `WRITE_W_PREVIEW_BUILD` confirmed at runtime
 * against the running Firebase app's project (`confirmWriteWPreviewAtRuntime`, `writeWPreview.ts`): the
 * runtime half can only turn a preview OFF — a native binary carrying staging's `EXPO_PUBLIC_*` values
 * but production's GoogleService files is not a preview. Short-circuited on the build constant, so it
 * costs nothing outside a preview build.
 */
export const WRITE_W_CAPABILITY: WriteWCapability = deriveWriteWCapability(
  WRITE_W_SEAM,
  W_WRITER_MANIFEST,
  WRITE_W_PREVIEW_BUILD && confirmWriteWPreviewAtRuntime(loadFirebaseApp),
)
